import { CalendarEvent } from '@karve.it/interfaces/calendarEvents';
import { isNil } from 'lodash';

import pluralize from 'pluralize';

import {
  BaseLocationFragment,
  FullJobFragment,
  Job_CalendarEventsFragment,
  Job_LocationsFragment,
} from '../../generated/graphql.generated';

export function remainingBalance<
  T extends { total: number; paidTotal: number }
>(balance: T, dollars = true) {
  const remainingBalanceCents = balance ? balance.total - balance.paidTotal : 0;

  return dollars ? remainingBalanceCents / 100 : remainingBalanceCents;
}

export function hasUncompletedEvents(job: Job_CalendarEventsFragment) {
  return job?.events?.some(
    (e) =>
      e.status !== 'completed' &&
      e.status !== 'required' &&
      e.status !== 'cancelled'
  );
}

export function getJobLocation(job: Job_LocationsFragment, type: string) {
  if (!job?.locations) {
    return;
  }

  const location = job.locations.find((l) => l.locationType === type);

  return location?.location;
}

//when lead comes from OBE addressLineOne is not always filled correct
//if it's not filled, display area code instead
export function formatLocationToDisplay(
  location: Partial<BaseLocationFragment>,
  skipAddressLineTwo = false,
) {
  const addressLineOne = location?.addressLineOne?.trim();
  const addressLineTwo = location?.addressLineTwo?.trim();
  const areaCode = location?.areaCode;
  const city = location?.city;

  if (addressLineOne) {
    const addressLineTwoPart = !skipAddressLineTwo && addressLineTwo ? addressLineTwo + ' - ' : '';
    return addressLineTwoPart + addressLineOne;
  } else {
    return (areaCode || '') + ' ' + (city || '');
  }
}

export function getEventCompareFn<T extends keyof CalendarEvent>(property: T) {
    return function (a, b): number {
      const aValue = a?.[property];
      const bValue = b?.[property];

      // Handle cases where the property is null or undefined
      if (aValue == null && bValue == null) {
        return 0; // Both are null or undefined, considered equal
      }
      if (aValue == null) {
        return 1; // `a` is null/undefined, so `b` comes first
      }
      if (bValue == null) {
        return -1; // `b` is null/undefined, so `a` comes first
      }

      // Compare values
      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }

      return 0; // Values are equal
    };
  }

type JobWithSharedZones = Pick<FullJobFragment, 'sharedZones'>;

export function isJobSharedToZone(job?: JobWithSharedZones, zone?: string) {
  if (!job || !zone) {
    return false;
  }

  return Boolean(job?.sharedZones?.includes(zone));
}

// Function to process location fields and build details string
//
export function getBuildingDetails(locationFields) {
  return [
    locationFields.dwellingType,
    locationFields.bedrooms && `${locationFields.bedrooms} BR`,
    locationFields.sqft && `${locationFields.sqft} sqft`,
  ]
    .filter(Boolean)
    .join(' | ');
}

export function getAccessInformation(locationFields) {
  const getElevatorDescription = (elevators, elevatorsBookable) => {
    let elevatorsInfo = '';
    if (!elevators || elevators === 'None') {
      elevatorsInfo += 'No Elevator'
    } else elevatorsInfo += `Elevator (${elevators})`;
    if (elevatorsBookable) {
      elevatorsInfo += ' | bookable'
    }
    return elevatorsInfo
  };

  const getStairsDescription = (stairs) => {
    if (isNil(stairs)) return undefined;
    if (stairs === 0) return 'No Stairs';
    if (stairs === 0.5) return 'Half Flight of Stairs (Up)';
    if (stairs === -0.5) return 'Half Flight of Stairs (Down)';
    return pluralize('stairs', stairs, true);
  };

  return [
    getElevatorDescription(locationFields.elevators, locationFields.elevatorsBookable),
    getStairsDescription(locationFields.stairs),
  ]
    .filter(Boolean)
    .join(' | ');
}

export function convertToDate(timeline: string): Date {
  if (!timeline) {
      return null;
  }

  const [year, month, day] = timeline.split('-').map(part => parseInt(part, 10));

  return new Date(year, month - 1, day);
}

export function getTimelineV2(timelineValue: Date): { timeline: string; timelineDays: number } {
  if (!timelineValue) {
    return {
      timeline: undefined,
      timelineDays: undefined,
    };
  }

  const start: Date = timelineValue;
  const timelineDays = 0;

  const timeline = [
    `${start.getFullYear()}`,
    `${(start.getMonth() + 1).toString().padStart(2, '0')}`,
    `${start.getDate().toString().padStart(2, '0')}`,
  ].join('-');

  return {
    timeline,
    timelineDays,
  };
}
